import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  isAgeBackfillPromptEnabled,
  isAgeGateStaffRole,
  isNewAuthAccount,
  shouldPromptAgeGate,
} from "@/lib/ageGate";

type Lookup = { userId: string | null; hasRow: boolean | null; isStaff: boolean; failed: boolean };
const EMPTY_LOOKUP: Lookup = { userId: null, hasRow: null, isStaff: false, failed: false };

export function useAgeVerificationGate() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const userId = user?.id ?? null;
  const [lookup, setLookup] = useState<Lookup>(EMPTY_LOOKUP);

  useEffect(() => {
    let cancelled = false;
    setLookup(EMPTY_LOOKUP);
    if (authLoading || !isAuthenticated || !userId) return;
    Promise.all([
      supabase.from("user_age_verification").select("id").eq("user_id", userId).maybeSingle(),
      supabase.from("user_roles").select("role").eq("user_id", userId),
    ]).then(([rowRes, roleRes]) => {
      if (cancelled) return;
      const failed = !!rowRes.error || !!roleRes.error;
      setLookup({
        userId,
        hasRow: failed ? null : !!rowRes.data,
        isStaff: !failed && (roleRes.data ?? []).some((r) => isAgeGateStaffRole(String(r.role))),
        failed,
      });
    }).catch(() => {
      if (!cancelled) setLookup({ ...EMPTY_LOOKUP, userId, failed: true });
    });
    return () => { cancelled = true; };
  }, [authLoading, isAuthenticated, userId, user]);

  // An old account's successful lookup cannot authorize a new account.
  const current = lookup.userId === userId;
  const lookupFailed = current && lookup.failed;
  const isStaff = current && lookup.isStaff;
  const hasRow = current ? lookup.hasRow : null;
  const isNew = isNewAuthAccount(user);
  const backfillEnabled = isAgeBackfillPromptEnabled();
  const ready = !authLoading && (!isAuthenticated || (hasRow !== null && !lookupFailed));
  const promptRequired =
    !!isAuthenticated &&
    ready &&
    shouldPromptAgeGate({
      hasVerificationRow: !!hasRow,
      isStaff,
      isNewAccount: isNew,
      backfillEnabled,
    });

  return {
    ready, promptRequired, isStaff,
    hasVerificationRow: hasRow === true,
    isNewAccount: isNew,
    lookupFailed,
    backfillEnabled,
  };
}
